import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface ProductData {
  id: string | number;
  code: string;
  name: string;
  total_qty?: number;
}

interface CategoryData {
  id: string | number;
  code: string;
  name: string;
  total_qty?: number;
  products?: ProductData[];
}

interface DepartmentData {
  id: string | number;
  code: string;
  name: string;
  total_qty?: number;
  categories?: CategoryData[];
}

interface ImpExpLevelQtyReportProps {
  reportKey: string;
  filters: any;
}

const ImpExpLevelQtyReport: React.FC<ImpExpLevelQtyReportProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<DepartmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [consignmentType, setConsignmentType] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await reportApi.generateReport(filters);

        if (filters.type) {
          setConsignmentType(filters.type);
        }

        // Transform the API response and calculate totals
        const departments: DepartmentData[] = (response?.report?.data || []).map((dept: any) => {
          const categories = (dept.categories || []).map((category: any) => {
            const products = (category.products || []).map((product: any) => ({
              id: product.product_id,
              code: product.product_code,
              name: product.product_name,
              total_qty: product.total_qty || 0,
            }));

            // Calculate category total by summing all product quantities
            const categoryTotal = products.reduce((sum: any, product: any) => sum + (product.total_qty || 0), 0);

            return {
              id: category.category_id,
              code: category.category_code,
              name: category.category_name,
              total_qty: categoryTotal,
              products: products,
            };
          });

          // Calculate department total by summing all category totals
          const departmentTotal = categories.reduce((sum: any, category: any) => sum + (category.total_qty || 0), 0);

          return {
            id: dept.department_id,
            code: dept.department_code,
            name: dept.department_name,
            total_qty: departmentTotal,
            categories: categories,
          };
        });

        setData(departments);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch Import/Export Level Quantity data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reportKey, filters]);

  const getCountColumnTitle = () => {
    if (consignmentType === 'IMPORT') return 'Import Qty';
    if (consignmentType === 'EXPORT') return 'Export Qty';
    return 'Qty';
  };

  const columns: any[] = [
    {
      title: 'Department / Category / Product',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: getCountColumnTitle(),
      dataIndex: 'total_qty',
      key: 'total_qty',
      render: (value: number | undefined) => (value !== undefined ? value.toLocaleString('en-LK') : '-'),
    },
  ];

  // Recursive transformation for nested rows (Department → Category → Product)
  const transformData = (departments: DepartmentData[]) =>
    departments.map((dept) => ({
      key: dept.id,
      name: `${dept.code} - ${dept.name}`,
      total_qty: dept.total_qty,
      children: dept.categories?.map((category) => ({
        key: `${dept.id}-${category.id}`,
        name: `${category.code} - ${category.name}`,
        total_qty: category.total_qty,
        children: category.products?.map((product) => ({
          key: `${dept.id}-${category.id}-${product.id}`,
          name: `${product.code} - ${product.name}`,
          total_qty: product.total_qty,
        })),
      })),
    }));

  const tableData = transformData(data);

  return (
    <Table
      title={() => (
        <div style={{ textAlign: 'center', fontWeight: 700 }}> L1 & L2 Levels by Quantity per Department</div>
      )}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      bordered
      loading={loading}
      expandable={{
        defaultExpandAllRows: true,
      }}
    />
  );
};

export default ImpExpLevelQtyReport;
